// src/components/modals/AddRawMaterialModal.jsx
import { useState, useEffect } from 'react'
import { X, Loader2, ChevronDown } from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import RawMaterialsService from '../../services/rawMaterialsService'
import { RawMaterialModel } from '../../utils/rawMaterialModel'

const AddRawMaterialModal = ({ isOpen, onClose, onMaterialAdded, units = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    unit: '',
    threshold: '',
    description: ''
    // Removed isActive since backend defaults to true
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        code: '',
        unit: '',
        threshold: '',
        description: ''
        // Removed isActive since backend defaults to true
      })
      setErrors({})
    }
  }, [isOpen])

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Auto-generate code from name
  const generateCode = (name) => {
    return name
      .toUpperCase()
      .replace(/[^A-Z0-9\s]/g, '')
      .replace(/\s+/g, '')
      .substring(0, 10)
  }

  // Handle name change with auto-code generation
  const handleNameChange = (e) => {
    const value = e.target.value
    setFormData(prev => ({
      ...prev,
      name: value,
      // Auto-generate code if code field is empty
      code: !prev.code ? generateCode(value) : prev.code
    }))

    // Clear error when user starts typing
    if (errors.name) {
      setErrors(prev => ({
        ...prev,
        name: ''
      }))
    }
  }

  // Remove handleStatusToggle since status is not editable in add modal

  // Validate form using the model
  const validateForm = () => {
    const validation = RawMaterialModel.validate(formData)
    setErrors(validation.errors)
    return validation.isValid
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      // console.log('❌ Form validation failed:', errors)
      // return
    }

    try {
      setLoading(true)

      // Prepare data for API
      const materialData = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        unit: formData.unit, // Unit ID is required for creation
        threshold: Number(formData.threshold),
        description: formData.description.trim()
        // isActive is not sent in creation - defaults to true on backend
      }

      console.log('📤 Creating raw material:', materialData)

      const response = await RawMaterialsService.createRawMaterial(materialData)

      if (response.success) {
        console.log('✅ Raw material created successfully:', response)

        // Reset form
        setFormData({
          name: '',
          code: '',
          unit: '',
          threshold: '',
          description: ''
        })
        setErrors({})

        // Notify parent component
        const createdMaterial = response.data?.rawMaterial || { ...materialData, _id: Date.now().toString() }
        onMaterialAdded(createdMaterial)

        // Close modal
        onClose()
      } else {
        console.log('⚠️ Creation failed with response:', response)
        // Handle API error
        if (response.errors) {
          setErrors(response.errors)
        } else {
          setErrors({ general: response.message || 'Failed to create raw material' })
        }
      }
    } catch (error) {
      console.error('❌ Error creating raw material:', error)

      // Enhanced error handling
      let errorMessage = 'Failed to create raw material. Please try again.'

      if (error.message.includes('already exists')) {
        errorMessage = 'A raw material with this name or code already exists.'
      } else if (error.message.includes('validation')) {
        errorMessage = 'Please check your input and try again.'
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      }

      setErrors({
        general: errorMessage
      })
    } finally {
      setLoading(false)
    }
  }

  // Don't render if not open
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <h2 className="text-xl font-semibold text-gray-900">Add Raw Material</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* General Error */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {errors.general}
            </div>
          )}

          {/* Material Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Material name *
            </label>
            <Input
              name="name"
              type="text"
              placeholder="e.g. Espresso Beans"
              value={formData.name}
              onChange={handleNameChange}
              className={errors.name ? 'border-red-300 focus:border-red-500' : ''}
              disabled={loading}
              required
            />
            {errors.name && (
              <p className="text-red-600 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Material Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Material Code *
            </label>
            <Input
              name="code"
              type="text"
              placeholder="e.g. EB1200"
              value={formData.code}
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase()
                handleInputChange(e)
              }}
              className={errors.code ? 'border-red-300 focus:border-red-500' : ''}
              disabled={loading}
              required
            />
            {errors.code && (
              <p className="text-red-600 text-sm mt-1">{errors.code}</p>
            )}
            {/* <p className="text-xs text-gray-500 mt-1">
              Code will be automatically generated from name, or you can enter your own
            </p> */}
          </div>

          {/* Unit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unit *
            </label>
            <div className="relative">
              <select
                name="unit"
                value={formData.unit}
                onChange={handleInputChange}
                disabled={loading}
                className={`w-full p-3 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed appearance-none ${errors.unit ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-200'
                  }`}
                required
              >
                <option value="">Select unit</option>
                {units.length === 0 ? (
                  <option value="" disabled>Loading units...</option>
                ) : (
                  units
                    .filter(unit => unit.isActive)
                    .map(unit => (
                      <option key={unit._id} value={unit._id}>
                        {unit.name} ({unit.abbreviation})
                      </option>
                    ))
                )}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
            {errors.unit && (
              <p className="text-red-600 text-sm mt-1">{errors.unit}</p>
            )}
            {units.length === 0 && (
              <p className="text-yellow-600 text-sm mt-1">
                Loading units... Please wait.
              </p>
            )}
            {units.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                {units.filter(unit => unit.isActive).length} active units available
              </p>
            )}
          </div>

          {/* Threshold */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Threshold *
            </label>
            <Input
              name="threshold"
              type="number"
              placeholder="e.g. 200"
              value={formData.threshold}
              onChange={handleInputChange}
              className={errors.threshold ? 'border-red-300 focus:border-red-500' : ''}
              disabled={loading}
              min="0"
              step="1"
              required
            />
            {errors.threshold && (
              <p className="text-red-600 text-sm mt-1">{errors.threshold}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Low stock alert threshold quantity
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              rows={3}
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter material description..."
              disabled={loading}
              className={`w-full p-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed ${errors.description ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
                }`}
            />
            {/* {errors.description && (
              <p className="text-red-600 text-sm mt-1">{errors.description}</p>
            )} */}
          </div>

          {/* Status - Remove from Add Modal since backend defaults to active */}
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <div className="flex items-center">
              <div className="relative inline-flex h-6 w-11 items-center rounded-full bg-orange-500">
                <span className="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out translate-x-6" />
              </div>

            </div>

          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-black hover:bg-gray-800 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Raw Material'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddRawMaterialModal